import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWith } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWith(data.user);
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
      const dest = location.state?.from || (data.user.role === "admin" ? "/admin" : "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      if (err?.response?.status === 423) {
        setLocked(true);
        toast.error("Account locked. Please recover your account.");
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16 md:py-24" data-testid="login-page">
      <div className="bg-white rounded-3xl border border-[#EDF1EF] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
        <span className="w-12 h-12 rounded-2xl bg-[#D9E4E1] flex items-center justify-center mb-5">
          <LogIn className="w-6 h-6 text-[#4A7065]" />
        </span>
        <h1 className="font-heading text-3xl text-slate-900">Welcome back</h1>
        <p className="text-sm text-slate-500 mt-1">
          Log in to book appointments and access your medical records securely.
        </p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <Label htmlFor="login-email">Email or Unique ID</Label>
            <Input
              id="login-email"
              data-testid="login-email-input"
              type="text"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com or DRPRXXXXXXXXXX"
              className="mt-1.5 rounded-xl"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password">Password</Label>
              <Link
                to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                data-testid="login-forgot-link"
                className="text-xs text-[#2C557E] hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="login-password"
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 rounded-xl"
            />
          </div>
          <Button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6"
          >
            {loading ? "Logging in…" : "Log In"}
          </Button>
        </form>
        {locked && (
          <div data-testid="login-locked-banner" className="mt-5 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-semibold mb-1">Your account is locked.</p>
            <p>For safety, we lock accounts after several failed login attempts. Please request account recovery — our clinic team will review and approve a password reset.</p>
            <Link
              to={`/recover-account${email ? `?email=${encodeURIComponent(email)}` : ""}`}
              data-testid="login-recover-link"
              className="mt-3 inline-block rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] text-white text-xs font-semibold px-4 py-2"
            >
              Recover account
            </Link>
          </div>
        )}
        <p className="text-sm text-slate-500 text-center mt-6">
          New patient?{" "}
          <Link to="/register" data-testid="login-register-link" className="text-[#2C557E] font-medium hover:underline">
            Register here
          </Link>
        </p>
        {!locked && (
          <p className="text-xs text-slate-400 text-center mt-3">
            Locked out?{" "}
            <Link to="/recover-account" data-testid="login-recover-link-soft" className="text-[#2C557E] hover:underline">
              Recover account
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
